// Colour extraction for the workshop cutting view. Colour is embedded in the
// variation text (and formatted differently per platform), so we match against
// EG's known felt palette rather than trying to parse labels.
//
// PROVISIONAL palette — confirm/adjust with the owner (and later make editable
// in Settings, like the delivery rules). Multi-word colours must precede their
// single-word substrings so "Pale Blue" matches before "Blue".

// Default felt palette as { name, hex } pairs. Editable in Settings; each colour
// now carries its own swatch so any colour you add shows its true colour.
export const DEFAULT_COLOURS = [
  { name: 'Pale Blue', hex: '#bcd6ec' },
  { name: 'Pale Pink', hex: '#f3d4e0' },
  { name: 'Pale Grey', hex: '#d3d3d3' },
  { name: 'Navy', hex: '#1f3a5f' },
  { name: 'Turquoise', hex: '#3fb8af' },
  { name: 'Charcoal', hex: '#444444' },
  { name: 'Orange', hex: '#e08a3c' },
  { name: 'Purple', hex: '#7b539e' },
  { name: 'Yellow', hex: '#e8c844' },
  { name: 'Green', hex: '#5a9e5a' },
  { name: 'Blue', hex: '#2f6fb0' },
  { name: 'Mid Grey', hex: '#9a9a9a' },
  { name: 'Black', hex: '#1a1a1a' },
  { name: 'Red', hex: '#c0392b' },
  { name: 'Pink', hex: '#e08fb0' },
];

const GREY = '#cfcfcf';

// Just the names (for colour extraction and as the importer default).
export const COLOUR_PALETTE = DEFAULT_COLOURS.map((c) => c.name);
const DEFAULT_HEX = Object.fromEntries(DEFAULT_COLOURS.map((c) => [c.name.toLowerCase(), c.hex]));

/**
 * Resolve a colour name to a hex/CSS colour, in priority order:
 *  1) the (editable) palette's own hex for that name,
 *  2) a built-in default for the standard felt colours,
 *  3) a valid CSS colour name (e.g. "indigo", "teal"),
 *  4) neutral grey.
 * `palette` may be ['Navy', …] or [{ name, hex }, …].
 */
export function colourHex(name, palette) {
  if (!name) return GREY;
  const key = String(name).toLowerCase();
  const entry = (palette || []).find((c) => typeof c === 'object' && c.name?.toLowerCase() === key);
  if (entry?.hex) return entry.hex;
  if (DEFAULT_HEX[key]) return DEFAULT_HEX[key];
  return cssColour(name) || GREY;
}

/** A sensible default hex when adding a new colour (its CSS colour, or grey). */
export function guessHex(name) {
  const key = String(name || '').toLowerCase();
  return DEFAULT_HEX[key] || cssColour(name) || GREY;
}

// Resolve a CSS colour name (e.g. "indigo") to a hex string, or null if invalid.
// Uses canvas fillStyle, which normalises recognised colours to #rrggbb.
let _ctx;
function cssColour(name) {
  if (typeof document === 'undefined' || !name) return null;
  if (!_ctx) _ctx = document.createElement('canvas').getContext('2d');
  if (!_ctx) return null;
  // A name is valid if it resolves the same regardless of the prior value.
  _ctx.fillStyle = '#000000';
  _ctx.fillStyle = String(name);
  const a = _ctx.fillStyle;
  _ctx.fillStyle = '#ffffff';
  _ctx.fillStyle = String(name);
  const b = _ctx.fillStyle;
  return a === b && a.startsWith('#') ? a : null;
}

/** Names from a palette that may be strings or { name } objects. */
export const paletteNames = (palette) =>
  (palette || []).map((c) => (typeof c === 'string' ? c : c.name)).filter(Boolean);

// Treat the US spelling as the UK one.
const normalise = (c) => c.replace(/\bgray\b/gi, 'Grey');

// Longest names first so "Pale Blue" matches before "Blue".
const byLengthDesc = (a, b) => b.length - a.length;

/**
 * Find the first palette colour mentioned in `text`. Returns the canonical
 * colour or '' if none found. `palette` defaults to the provisional list.
 */
export function extractColour(text, palette = COLOUR_PALETTE) {
  if (!text) return '';
  const hay = normalise(String(text));

  // The felt colour is the value of the "Colour:" / "Color:" attribute. Scope the
  // search to that value (up to the next attribute's colon) so we ignore loop /
  // marker / stylus colours elsewhere, e.g. "Color: Gray / Marker loop: Mint Green".
  // If there's no such label (e.g. Shopify), fall back to the whole string.
  const labelled = hay.match(/colou?r[^:]*:\s*([^:]+)/i);
  const scope = labelled ? labelled[1] : hay;

  for (const colour of paletteNames(palette).sort(byLengthDesc)) {
    if (/^grey$/i.test(colour)) continue; // plain grey handled below
    const re = new RegExp(`(^|[^a-z])${colour.replace(/\s+/g, '\\s+')}([^a-z]|$)`, 'i');
    if (re.test(scope)) return colour;
  }
  // A grey with no modifier (light/dark/mid/pale/charcoal…) -> "Mid Grey".
  if (hasBareGrey(scope)) return 'Mid Grey';
  return '';
}

// True if the text contains "grey" (after gray->grey) not preceded by a modifier.
const GREY_MODIFIER = /(pale|light|dark|mid|deep|slate|gun|charcoal|ash)\s*$/i;
function hasBareGrey(hay) {
  const re = /(^|[^a-z])grey([^a-z]|$)/gi;
  let m;
  while ((m = re.exec(hay)) !== null) {
    const before = hay.slice(0, m.index + m[1].length);
    if (!GREY_MODIFIER.test(before)) return true;
    re.lastIndex = m.index + 1;
  }
  return false;
}

/** Colour for a line item (variation is the richest source; product as backup). */
export function lineItemColour(lineItem, palette = COLOUR_PALETTE) {
  const text = `${lineItem.variation || ''} ${lineItem.product || ''} ${lineItem.buyerNotes || ''}`;
  return extractColour(text, palette) || 'Unknown colour';
}
